import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RightsAccess } from '../../helper';
import { PRODUCT_INFORMATION_MENU_ITEM_MAP, normalizeItemName } from '../../../constants/module-order-menu-mapping';
import MenuItem from '../MenuItem';
import { CriticalPathIcon, PlaceholderManagementIcon, PlanningIcon, ProductsIcon, RangePlanIcon } from '../../../bombiix-icon';

function PlaceholderUrl({ isMobileMenu }) {
  const reducer = useSelector((reducerstate) => reducerstate);
  const history = useHistory();
  const pathName = history.location.pathname;

  const {
    Users: { moduleAccess, sectionAccess },
    UserDetails: { isSupplierLoggedIn },
    SystemMastersDetails: { moduleOrderConfigs = [] },
  } = reducer;
  const { planningBottomUpRight, planningTopsDownRight, placeholderRight, rangePlanRight, productCriticalPathRight, operationalCriticalPathRight } =
    RightsAccess();

  const { planningAccess, placeholderAccess, rangePlanAccess, productsAccess, criticalPathAccess } = moduleAccess;

  const config = useMemo(() => (moduleOrderConfigs || []).find((c) => c.moduleId === 'productInformation'), [moduleOrderConfigs]);
  const orderedItems = useMemo(
    () => (config?.items ? [...config.items].filter((i) => i.isActive !== false).sort((a, b) => (a.sequence || 0) - (b.sequence || 0)) : null),
    [config],
  );

  const getLinkTo = (routeConfig) => {
    if (routeConfig.linkResolver) {
      if (routeConfig.permissionKey === 'planning') {
        return routeConfig.linkResolver(sectionAccess, planningTopsDownRight);
      }
      if (routeConfig.permissionKey === 'criticalPath') {
        return routeConfig.linkResolver(sectionAccess, isSupplierLoggedIn, operationalCriticalPathRight);
      }
    }
    return routeConfig.path;
  };

  const canShow = (routeConfig) => {
    if (routeConfig.permissionKey === 'products') return productsAccess;
    if (routeConfig.permissionKey === 'planning') return planningAccess && !isSupplierLoggedIn && (planningTopsDownRight || planningBottomUpRight);
    if (routeConfig.permissionKey === 'placeholder') return placeholderAccess && !isSupplierLoggedIn && placeholderRight;
    if (routeConfig.permissionKey === 'rangePlan') return rangePlanAccess && rangePlanRight && !isSupplierLoggedIn;
    if (routeConfig.permissionKey === 'criticalPath') {
      return (
        criticalPathAccess &&
        (sectionAccess?.criticalPath?.operationalAccess || sectionAccess?.criticalPath?.productAccess) &&
        (productCriticalPathRight || operationalCriticalPathRight)
      );
    }
    return false;
  };

  const items = useMemo(() => {
    if (!orderedItems || orderedItems.length === 0) return null;
    return orderedItems
      .map((item) => {
        const key = normalizeItemName(item.itemName);
        const routeConfig = PRODUCT_INFORMATION_MENU_ITEM_MAP[key];
        if (!routeConfig || !canShow(routeConfig)) return null;
        return (
          <MenuItem
            key={item.itemId || key}
            activeClassList={routeConfig.activeClassList}
            pathName={pathName}
            linkTo={getLinkTo(routeConfig)}
            iconClass={routeConfig.iconClass}
            titleKey={routeConfig.titleKey}
            isMobileMenu={isMobileMenu}
          />
        );
      })
      .filter(Boolean);
  }, [
    orderedItems,
    pathName,
    isMobileMenu,
    sectionAccess,
    isSupplierLoggedIn,
    planningTopsDownRight,
    planningBottomUpRight,
    operationalCriticalPathRight,
    productsAccess,
    planningAccess,
    placeholderAccess,
    placeholderRight,
    rangePlanAccess,
    rangePlanRight,
    criticalPathAccess,
    productCriticalPathRight,
  ]);

  if (items && items.length > 0) {
    return <ul>{items}</ul>;
  }

  return (
    <ul>
      {productsAccess && (
        <MenuItem
          activeClassList={['/development-order-overview', '/modules-bulk-edit']}
          pathName={pathName}
          linkTo="/development-order-overview"
          IconComponent={ProductsIcon}
          titleKey="PLACEHOLDER_URL.PRODUCTS"
          isMobileMenu={isMobileMenu}
        />
      )}
      {planningAccess && !isSupplierLoggedIn && (planningTopsDownRight || planningBottomUpRight) && (
        <MenuItem
          activeClassList={['/planning/planning-actual', '/planning/planning-bottomup']}
          pathName={pathName}
          linkTo={sectionAccess?.planning?.topDownAccess && planningTopsDownRight ? '/planning/planning-actual' : '/planning/planning-bottomup'}
          IconComponent={PlanningIcon}
          titleKey="PLACEHOLDER_URL.PLANNING"
          isMobileMenu={isMobileMenu}
        />
      )}
      {placeholderAccess && !isSupplierLoggedIn && placeholderRight && (
        <MenuItem
          activeClassList={['/placeholder-management']}
          pathName={pathName}
          linkTo="/placeholder-management"
          IconComponent={PlaceholderManagementIcon}
          titleKey="PLACEHOLDER_URL.PLACEHOLDER_MANAGEMENT"
          isMobileMenu={isMobileMenu}
        />
      )}
      {rangePlanAccess && rangePlanRight && !isSupplierLoggedIn && (
        <MenuItem
          activeClassList={['/rangeplan/card-view', '/rangeplan/add', '/rangeplan/edit', '/rangeplan/product-list', '/rangeplan/archived']}
          pathName={pathName}
          linkTo="/rangeplan/card-view"
          IconComponent={RangePlanIcon}
          titleKey="PLACEHOLDER_URL.RANGE_PLAN"
          isMobileMenu={isMobileMenu}
        />
      )}
      {criticalPathAccess &&
        (sectionAccess?.criticalPath?.operationalAccess || sectionAccess?.criticalPath?.productAccess) &&
        (productCriticalPathRight || operationalCriticalPathRight) && (
          <MenuItem
            activeClassList={['operational-cp', '/product-cp']}
            pathName={pathName}
            linkTo={sectionAccess?.criticalPath?.operationalAccess && !isSupplierLoggedIn && operationalCriticalPathRight ? '/operational-cp' : '/product-cp'}
            IconComponent={CriticalPathIcon}
            titleKey="PLACEHOLDER_URL.CRITICAL_PATHS"
            isMobileMenu={isMobileMenu}
          />
        )}
    </ul>
  );
}

export default PlaceholderUrl;
